/**
 * แปลงข้อมูลจาก CRM module เป็นรูปแบบที่หน้าจอในงานออกแบบใช้
 *
 * ที่นี่คือจุดเดียวที่ยอมรับความจริงว่า CRM ยังไม่มีอะไรบ้าง
 * หน้าจอไม่ต้องรู้เรื่องนี้เลย และเมื่อ backend เติมของที่ขาดมาให้
 * แก้แค่ไฟล์นี้ไฟล์เดียว
 *
 * รายการที่ยังไม่มีใน CRM และวิธีรับมือชั่วคราว — ดู เอกสารวิเคราะห์ช่องว่างของ API ที่ทีมเก็บไว้ภายใน
 */

import { SLA_HOURS } from '../screens/mockData';
import type { AnomalyFlag, Channel, Claim as UiClaim, MemberSummary, Tier as UiTierName } from '../screens/mockData';
import { formatThaiDate } from '../components';
import type { AuditEntry } from '../components';
import type {
  Coupon as UiCoupon, CouponKind as UiCouponKind, CouponStatus as UiCouponStatus,
  Privilege as UiPrivilege, PrivilegeBenefitKind as UiPrivilegeBenefitKind,
  PrivilegeRecurrence as UiPrivilegeRecurrence,
  PrivilegeStatus as UiPrivilegeStatus, Reward as UiReward, RewardFeature,
  RewardType as UiRewardType, StackRule as UiStackRule, TierId as UiTierId,
} from '../screens/loyaltyData';
import type { VoucherTemplate, VoucherTemplatePatch } from './bilClient';
import type {
  Account,
  AuditLogEntry,
  AdminBenefitCode, AdminBenefitCodePatch,
  Claim as CrmClaim, ClaimActionLog, EarningPolicy, MiniAccount, Reward as CrmReward,
  RewardPatch, RewardType as CrmRewardType, Tier as CrmTier,
} from './crmClient';

/* ------------------------------------------------------------------ คำขอ */

const PLATFORM_TO_CHANNEL: Record<string, Channel> = {
  SHOPEE: 'Shopee',
  LAZADA: 'Lazada',
  TIKTOK: 'TikTok Shop',
};

/**
 * ชื่อประเภทคำขอ ใช้เป็นทางสำรองเมื่อไม่มีทั้ง platform และ order_location
 * ⚠️ ต้องตรงกับชื่อประเภทที่แอปลูกค้าแสดง
 *    ลูกค้ากับเจ้าหน้าที่ต้องเห็นคำเดียวกันเวลาคุยกันเรื่องคำขอใบเดียวกัน
 */
const TYPE_TO_CHANNEL: Record<string, Channel> = {
  RECEIPT: 'ใบเสร็จจากร้านค้า',
  BANK_TRANSFER: 'โอนเงิน',
  PLATFORM: 'ร้านค้าออนไลน์',
  OTHER: 'ช่องทางอื่น',
};

/**
 * ช่องทางการซื้อ — **แตกตาม `type` ซึ่งเป็นตัวบอกว่าคำขอมาจากทางไหน**
 *
 *   PLATFORM  → ชื่อมาร์เก็ตเพลสจาก `platform`
 *   ที่เหลือ   → `order_location` ซึ่งคือชื่อช่องทางที่ลูกค้าเลือกในแอป
 *
 * 🔴 เดิมดูแค่ `platform` แล้วตกมาที่ "อื่น ๆ" ทุกคำขอที่ไม่ได้มาจากมาร์เก็ตเพลส
 *    ซึ่งผิด เพราะ **`order_location` คือชื่อช่องทาง ไม่ใช่ที่อยู่สาขา**
 *    (แอปลูกค้าส่งชื่อช่องทางมาเก็บลงฟิลด์นี้ตรง ๆ)
 *    ผลคือลูกค้าเลือก "งานออกบูธ" แต่เจ้าหน้าที่เห็น "อื่น ๆ" — คุยกันคนละเรื่อง
 *
 * ทางสำรองมีไว้กันข้อมูลไม่ครบ ไม่ใช่เส้นทางปกติ
 *   คำขอเก่าที่ส่งก่อน backend เพิ่ม `order_location` จะไม่มีค่าในฟิลด์นี้
 *   และ `platform` เป็น null ได้แม้ `type` จะเป็น PLATFORM
 *   ทั้งสองกรณีตกมาที่ชื่อประเภท ซึ่งยังบอกอะไรได้มากกว่าเว้นว่าง
 *
 * ⚠️ ค่าใน TYPE_TO_CHANNEL ต้องตรงกับชื่อประเภทที่แอปลูกค้าแสดง
 *    ถ้าฝั่งแอปเปลี่ยน ต้องตามแก้ที่นี่
 */
function channelOf(c: CrmClaim): Channel {
  const location = c.orderLocation?.trim();
  const fallback = TYPE_TO_CHANNEL[c.type] ?? 'ช่องทางอื่น';

  if (c.type === 'PLATFORM') {
    return (c.platform && PLATFORM_TO_CHANNEL[c.platform]) || location || fallback;
  }
  return location || fallback;
}

const hoursSince = (iso: string) =>
  Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 3_600_000));

/**
 * สถานะของ CRM มีแค่ 3 ค่า ส่วนหน้าจอมี 5
 *
 *   review   ⛔ CRM ไม่มีแนวคิด "กำลังตรวจ" — ไม่มีทางรู้ จึงไม่ใช้
 *   overdue  ✅ **คำนวณเองได้** จากอายุคำขอเทียบกับ SLA
 *            ไม่ใช่การเดา เพราะ SLA เป็นกติกาฝั่งเรา ไม่ใช่ข้อมูลจาก backend
 */
function statusOf(c: CrmClaim): UiClaim['status'] {
  if (c.status === 'APPROVED') return 'approved';
  if (c.status === 'REJECTED') return 'rejected';
  return hoursSince(c.created) > SLA_HOURS ? 'overdue' : 'pending';
}

/**
 * ชื่อผู้ใช้บางบัญชีเป็นรหัสที่เครื่องสร้าง ไม่ใช่ชื่อที่คนอ่านออก
 *
 * สมาชิกที่สมัครผ่าน LINE ได้ username หน้าตาแบบ
 * `u6fbaf4354ec2544cc9e991b51eb9d969-dummy.com` ซึ่งเอาไปแสดงในประวัติแล้ว
 * เจ้าหน้าที่อ่านไม่รู้เรื่องเลย (เจอจริงในหน้าประวัติคะแนน)
 *
 * จับสองรูปแบบ — ลงท้ายด้วย `-dummy.com` หรือเป็นสตริงยาวไม่มีช่องว่าง
 * ที่เป็นเลขฐานสิบหกล้วน
 */
function looksMachineGenerated(name: string): boolean {
  const n = name.trim();
  if (!n) return true;
  if (n.endsWith('-dummy.com')) return true;
  return /^u?[0-9a-f]{24,}$/i.test(n);
}

/**
 * ชื่อผู้ทำที่อ่านรู้เรื่อง
 *
 * ถ้าชื่อจริงก็เป็นรหัสเครื่องอีก (backend fallback ไป username เมื่อไม่มีชื่อใน profile)
 * ให้แสดง "สมาชิก #<id>" ซึ่ง**บอกตรง ๆ ว่ายังไม่มีชื่อ** ดีกว่าโชว์รหัสยาว ๆ
 * ที่ดูเหมือนข้อมูลเสีย
 */
function readableActor(rawName: string, fallbackId: number | null): string {
  if (!looksMachineGenerated(rawName)) return rawName.trim();
  return fallbackId === null ? 'ไม่ทราบชื่อ' : `สมาชิก #${fallbackId}`;
}

/**
 * ข้อมูลสมาชิกที่ CRM ให้ได้จริง
 *
 * ✅ **ชื่อจริงมากับคำขอแล้ว** (backend อัปเดต 25 ส.ค. 2569) — `account.fullName`
 *    backend ประกอบให้จาก patient ก่อน ถ้าว่างจึงใช้ profile ของ user
 *    ถ้าว่างอีกจึงตกมาที่ username จึงไม่มีทางได้ค่าว่าง
 *    เดิมต้องดึง loyalty-account ทั้ง division มา join เอง แล้วแสดงเป็น "สมาชิก #3"
 *
 * ⛔ ที่ยังไม่มี — ระดับสมาชิก คะแนนคงเหลือ ยอดสะสม และวันสมัคร
 *    เพราะ MiniLoyaltyAccount ที่ฝังมามีแค่ id · user · patient · full_name
 *    ค่าที่หน้าจอต้องใช้จึงส่ง 0 ไปแล้วให้หน้าจอซ่อนกล่องสถิติ
 */
function memberOf(acc: MiniAccount): MemberSummary {
  return {
    id: String(acc.user ?? acc.id),
    /* ⚠️ fullName ตกมาที่ username เมื่อ profile ไม่มีชื่อ ซึ่งของสมาชิกที่มาจาก LINE
       เป็นรหัสเครื่องยาว ๆ อ่านไม่ออก — แปลงเป็น "สมาชิก #<id>" แทน */
    name: readableActor(acc.fullName, acc.user),
    memberCode: `ACC-${String(acc.id).padStart(6, '0')}`,
    /* ⛔ ระดับสมาชิกไม่ได้ฝังมากับคำขอ — ต้องยิง loyalty-account แยกถึงจะได้ */
    tier: 'Silver' as UiTierName,
    points: 0,
    yearSpend: 0,
    joinedAt: '',
    /* ⛔ CRM ไม่มีสถิติประวัติการส่งคำขอเลยทั้งระบบ — ส่ง 0 แล้วให้หน้าจอซ่อนกล่องนี้ */
    claimsTotal: 0,
    claimsApproved: 0,
    claimsRejected: 0,
    claimsToday: 0,
  };
}

/**
 * ความผิดปกติที่ตรวจได้จากข้อมูลที่ backend ส่งมาจริง
 *
 * ตอนนี้มีชนิดเดียวคือ **ยอดที่ OCR อ่านได้ไม่ตรงกับที่ลูกค้ากรอก**
 *
 * ⛔ อีกสามชนิดที่งานออกแบบเผื่อไว้ยังทำไม่ได้ เพราะ backend ไม่มีข้อมูลให้
 *    duplicate-slip   ไม่มีการเทียบภาพหรือ hash สลิป — กันซ้ำด้วย order_id ตอนสร้างแทน
 *                     (ส่งซ้ำได้ 400 'Already claimed')
 *                     คำขอซ้ำจึงไม่เข้าคิวตั้งแต่แรก ไม่มีอะไรให้ตรวจที่นี่
 *    high-frequency   ไม่มีสถิติการส่งคำขอต่อช่วงเวลา
 *    amount-outlier   ไม่มีค่าเฉลี่ยให้เทียบ
 *
 * ⚠️ เทียบด้วยส่วนต่างไม่เกิน 0.01 บาท เพราะทั้งสองค่าเป็นทศนิยม 2 ตำแหน่ง
 *    การเทียบ === ตรง ๆ จะติดปัญหาการปัดเศษของ Number()
 */
function anomaliesOf(c: CrmClaim): AnomalyFlag[] {
  const out: AnomalyFlag[] = [];

  const ocrTotal = c.ocrData?.totalAmount;
  if (ocrTotal !== null && ocrTotal !== undefined && ocrTotal !== '') {
    const read = Number(ocrTotal);
    const declared = Number(c.amount);
    if (Number.isFinite(read) && Number.isFinite(declared) && Math.abs(read - declared) > 0.01) {
      out.push({
        kind: 'amount-mismatch',
        message:
          `ระบบอ่านยอดจากสลิปได้ ${read.toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท ` +
          `แต่ลูกค้ากรอก ${declared.toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท ` +
          '— ตรวจภาพสลิปก่อนตัดสิน',
      });
    }
  }

  return out;
}

/**
 * เติมข้อมูลสมาชิกจาก loyalty-account/<pk>/ ลงบน MemberSummary ที่คิวมีอยู่แล้ว
 *
 * คิว (point-claim/) ฝังบัญชีแบบย่อมาแค่ id · user · patient · full_name
 * ส่วนระดับสมาชิกและคะแนนคงเหลืออยู่ที่ loyalty-account/<pk>/ ซึ่งต้องยิงแยก
 * → โหลดเฉพาะรายการที่เจ้าหน้าที่เปิดดูจริง
 *
 * ⛔ ที่ยังเติมไม่ได้เพราะ backend ไม่มี: yearSpend · joinedAt ·
 *    สถิติคำขอ (claimsTotal / Approved / Rejected / Today)
 *    ปล่อยเป็น 0 ไว้เหมือนเดิม หน้าจอซ่อนกล่องสถิติเองเมื่อเป็น 0
 */
export function mergeAccountIntoMember(member: MemberSummary, acc: Account): MemberSummary {
  return {
    ...member,
    tier: tierNameOf(acc.currentTier?.name),
    /* คะแนนที่ใช้แลกของรางวัล ไม่ใช่คะแนนสำหรับเลื่อนระดับ — เจ้าหน้าที่ดูตัวนี้ตอนตัดสิน */
    points: Math.floor(Number(acc.rewardPoints ?? 0)),
  };
}

/** ชื่อระดับจาก CRM → ชื่อระดับที่หน้าจอใช้ · ไม่รู้จัก = Silver (ระดับต่ำสุด) */
function tierNameOf(name?: string | null): UiTierName {
  const key = (name ?? '').trim().toLowerCase();
  if (key === 'platinum') return 'Platinum' as UiTierName;
  if (key === 'gold') return 'Gold' as UiTierName;
  return 'Silver' as UiTierName;
}

export function toUiClaim(c: CrmClaim): UiClaim {
  const decided = c.status === 'APPROVED' || c.status === 'REJECTED';

  return {
    id: String(c.id),
    /* CRM ไม่มีรหัสที่อ่านออก — ประกอบจาก id เพื่อให้ค้นหาและอ่านออกเสียงได้ */
    code: `CLM-${String(c.id).padStart(5, '0')}`,
    kind: c.type === 'PLATFORM' ? 'order-id' : 'slip',
    status: statusOf(c),
    /* ⛔ CRM ไม่มีระบบตรวจอัตโนมัติ ทุกคำขอจึงเป็นงานที่คนต้องตรวจ */
    source: 'manual',
    channel: channelOf(c),
    member: memberOf(c.account),
    declaredAmount: Number(c.amount),
    /* ⛔ ไม่มีฟิลด์เก็บยอดที่เจ้าหน้าที่แก้ */
    verifiedAmount: undefined,
    submittedAt: c.created,
    ageHours: hoursSince(c.created),
    slipUrl: c.image ?? undefined,
    /* ⛔ ไม่มีภาพย่อ — ใช้รูปเต็มไปก่อน */
    slipThumbUrl: c.image ?? undefined,
    /* OCR เติม order_id ให้ตอนสร้างถ้าลูกค้าเว้นว่าง — แต่เผื่อกรณีที่ยังว่างอยู่ */
    orderNumber: c.orderId || c.ocrData?.orderId || undefined,
    autoCheckFailure: undefined,
    /* ⛔ ไม่มีผู้รับผิดชอบ */
    assigneeId: null,
    anomalies: anomaliesOf(c),
    /**
     * ผลการตัดสิน — **ต้องเลือกตามสถานะ ไม่ใช่ตามลำดับฟิลด์**
     *
     * ⚠️ backend **ไม่ล้างค่าเดิมตอน rollback** คำขอที่เคยอนุมัติแล้วย้อนกลับ
     *    แล้วปฏิเสธทีหลัง จะมีครบทั้ง approved_by/at · rollback_at · rejected_by/at
     *    (เจอจริงกับคำขอ id 8 บน staging — มีทั้งสามชุดพร้อมกัน)
     *
     *    เดิมเขียน `approvedBy ?? rejectedBy` ซึ่งหยิบ "ผู้อนุมัติ" มาแสดง
     *    บนคำขอที่ถูกปฏิเสธ — ผิดทั้งชื่อคนและเวลา และเป็นข้อมูลที่ใช้สอบสวนย้อนหลัง
     */
    decision: decided
      ? {
          at: (c.status === 'APPROVED' ? c.approvedAt : c.rejectedAt) ?? c.edited,
          byId: (c.status === 'APPROVED' ? c.approvedBy : c.rejectedBy)?.username ?? 'system',
          reasonLabel: c.rejectedReason || undefined,
          pointsGranted: 0,
        }
      : undefined,
  };
}

/* ------------------------------------------------------------ ระดับสมาชิก */

/** ชื่อระดับของ CRM เป็นข้อความอิสระ — จับคู่กับ id ที่หน้าจอเราใช้ */
export function tierIdFromName(name: string): 'silver' | 'gold' | 'platinum' {
  const n = name.trim().toLowerCase();
  if (n.includes('platinum')) return 'platinum';
  if (n.includes('gold')) return 'gold';
  return 'silver';
}

export interface UiTier {
  id: number;
  key: 'silver' | 'gold' | 'platinum';
  name: string;
  minSpend: number;
  maxSpend: number | null;
  /** ข้อความสิทธิ์ที่ผูกไว้ — มาจากหน้าผู้ดูแลระบบของ backend เท่านั้น แก้ที่นี่ไม่ได้ */
  benefits: Array<{ code: string; text: string; highlight: boolean }>;
}

export function toUiTier(t: CrmTier): UiTier {
  return {
    id: t.id,
    key: tierIdFromName(t.name),
    name: t.name,
    minSpend: Number(t.startPoint),
    maxSpend: t.endPoint === null ? null : Number(t.endPoint),
    benefits: (t.activeBenefitCodes ?? []).map((b) => ({
      code: b.code,
      text: b.description?.trim() || b.name,
      highlight: b.isHighlight,
    })),
  };
}

/* ------------------------------------------------------------ กติกาคะแนน */

export interface UiPointRule {
  id: number;
  isDefault: boolean;
  /** ซื้อกี่บาทได้ 1 คะแนน — CRM เก็บเป็นตัวคูณ จึงกลับค่าให้อ่านง่าย */
  spendPerPoint: number;
  rewardMultiplier: number;
  rewardExpiryDays: number;
  tierMultiplier: number;
  tierExpiryDays: number;
  startDate: string | null;
  endDate: string | null;
  editedAt: string;
  editedBy: string | null;
}

export function toUiPointRule(p: EarningPolicy): UiPointRule {
  const rm = Number(p.rewardMultiplier);
  return {
    id: p.id,
    isDefault: p.default,
    /* ตัวคูณ 0.01 = ซื้อ 100 บาทได้ 1 คะแนน — คนตั้งค่าอ่านแบบหลังเข้าใจง่ายกว่า */
    spendPerPoint: rm > 0 ? Math.round(1 / rm) : 0,
    rewardMultiplier: rm,
    rewardExpiryDays: p.rewardDurationDays,
    tierMultiplier: Number(p.tierMultiplier),
    tierExpiryDays: p.tierDurationDays,
    startDate: p.startDate,
    endDate: p.endDate,
    editedAt: p.edited,
    editedBy: p.editUser,
  };
}

/* ------------------------------------------------------- ประวัติการตรวจ */

/** ข้อความไทยของแต่ละการกระทำ — ไอคอนกำกับทุกตัว ไม่สื่อด้วยสีอย่างเดียว */
const ACTION_TEXT: Record<string, { action: string; icon: string }> = {
  CREATE: { action: 'ส่งคำขอสะสมคะแนน', icon: 'upload' },
  EDIT: { action: 'แก้ยอดในคำขอ', icon: 'edit' },
  APPROVE: { action: 'อนุมัติคำขอ', icon: 'check_circle' },
  REJECT: { action: 'ปฏิเสธคำขอ', icon: 'cancel' },
  ROLLBACK: { action: 'ย้อนการตัดสินกลับเป็นรอตรวจ', icon: 'undo' },
};

/**
 * แปลง action log ของ CRM เป็นประวัติที่หน้าจอแสดงได้ จัดกลุ่มตามคำขอ
 *
 * แยกว่าใครเป็นคนทำจาก id — ถ้าตรงกับเจ้าของบัญชีคือ **สมาชิก** ส่งคำขอเอง
 * จึงใช้ชื่อจริงจาก `account.fullName` แทน username ที่เป็นรหัสเครื่อง
 * ถ้าไม่ตรงคือ **เจ้าหน้าที่** ซึ่ง username อ่านรู้เรื่องอยู่แล้ว (เช่น DoctorPT2)
 *
 * ⚠️ **CRM ไม่เก็บค่าก่อน/หลังของการแก้** log มีแค่ว่าใครทำอะไรเมื่อไร
 *    `changes` จึงว่างเสมอ หน้าจอที่หวังจะเห็น "ยอด 500 → 450" จะไม่ได้
 *    ต่างจากข้อมูลจำลองที่ประกอบ changes ขึ้นมาเอง
 *
 * ⚠️ `datetime` ใน backend ใช้ `auto_now` ไม่ใช่ `auto_now_add`
 *    ถ้าแถว log ถูกเขียนซ้ำ เวลาจะขยับตาม ไม่ใช่เวลาที่เกิดเหตุการณ์จริงเสมอไป
 *
 * ⛔ log ไม่มีบทบาทจริงของผู้ทำ (CRM ยังไม่มีระบบบทบาท) — ป้ายที่ใส่จึงบอกแค่
 *    "สมาชิก" กับ "เจ้าหน้าที่" ซึ่งอนุมานจาก id ได้แน่นอน ไม่ใช่การเดาตำแหน่ง
 */
export function toAuditEntriesByClaim(logs: ClaimActionLog[]): Map<string, AuditEntry[]> {
  const byClaim = new Map<string, AuditEntry[]>();

  for (const log of logs) {
    const key = String(log.statable.id);
    const text = ACTION_TEXT[log.action] ?? { action: log.action, icon: 'history' };
    const account = log.statable.account;
    const isMember = log.user !== null && account.user !== null && log.user.id === account.user;

    const entry: AuditEntry = {
      id: String(log.id),
      at: log.datetime,
      actorName: !log.user
        ? 'ระบบ'
        : isMember
          ? readableActor(account.fullName, account.user)
          : readableActor(log.user.username, log.user.id),
      actorRole: !log.user ? '—' : isMember ? 'สมาชิก' : 'เจ้าหน้าที่',
      action: text.action,
      icon: text.icon,
      automated: !log.user,
      changes: [],
    };
    if (log.comment) entry.reason = log.comment;

    const list = byClaim.get(key);
    if (list) list.push(entry); else byClaim.set(key, [entry]);
  }

  /* ใหม่สุดอยู่บน — backend เรียง -datetime มาให้แล้ว แต่เรียงซ้ำกันพลาด */
  for (const list of byClaim.values()) {
    list.sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
  }
  return byClaim;
}

/* ------------------------------------------------------------ ของรางวัล */

/**
 * สิ่งที่งานออกแบบมีแต่ CRM ยังทำไม่ได้ — หน้าจอซ่อนส่วนของ key และแสดง label เป็นคำเตือน
 *
 * ⚠️ `per_user_limit` **รับค่าได้แต่ยังไม่ถูกบังคับ** ตอนแลก (รอทีม backend เพิ่มการตรวจ)
 *    ถ้าเปิดให้กรอก เจ้าหน้าที่จะเชื่อว่าจำกัดแล้วทั้งที่ลูกค้าแลกได้ไม่จำกัด จึงยังซ่อนไว้
 * ⚠️ `quantity` ของ CRM คือจำนวนคูปองที่ได้ต่อการแลก **ไม่ใช่สต๊อก**
 */
export const REWARD_UNSUPPORTED_FIELDS: Array<{ key: RewardFeature; label: string }> = [
  { key: 'stock', label: 'สต๊อกคงเหลือและการเตือนเมื่อใกล้หมด' },
  { key: 'limitPerPerson', label: 'จำกัดจำนวนครั้งต่อคน' },
  { key: 'redeemedTotal', label: 'จำนวนที่แลกไปแล้ว' },
];

/**
 * คำเตือนรายประเภทที่หน้าจอแสดงในฟอร์ม
 *
 * ⚠️ backend ยังไม่ได้ทำการส่งมอบของรางวัลประเภทสินค้า
 *    ลูกค้าที่แลกจะถูกตัดคะแนนแต่ระบบไม่สร้างอะไรให้ ต้องมีคนจัดส่งเอง
 */
export const REWARD_TYPE_NOTES: Partial<Record<UiRewardType, string>> = {
  product: 'ระบบยังไม่สร้างรายการจัดส่งให้อัตโนมัติ — ลูกค้าที่แลกจะถูกตัดคะแนนทันที ต้องมีผู้รับผิดชอบจัดส่งเองจนกว่าระบบจะพร้อม',
};

const REWARD_TYPE_FROM_CRM: Record<string, UiRewardType> = {
  VOUCHER: 'coupon',
  PRODUCT: 'product',
  FREE_SHIPPING: 'shipping',
};

const REWARD_TYPE_TO_CRM: Record<UiRewardType, CrmRewardType> = {
  coupon: 'VOUCHER',
  product: 'PRODUCT',
  shipping: 'FREE_SHIPPING',
};

/** ทศนิยมจาก DRF มาเป็นสตริง · ค่าว่างมาได้ทั้ง null และ '' */
const decimalOrNull = (v: string | null | undefined): number | null =>
  v === null || v === undefined || v === '' ? null : Number(v);

const toDecimal = (v: number | null | undefined): string | null =>
  v === null || v === undefined ? null : String(v);

/**
 * แปลงของรางวัลจาก CRM เป็นรูปที่หน้าจอใช้
 *
 * ค่าที่ CRM ยังไม่มีใส่ค่ากลางที่**ไม่หลอกตา** และหน้าจอซ่อนส่วนนั้นตาม REWARD_UNSUPPORTED_FIELDS
 *   stock: null       = ไม่จำกัด ไม่ใช่ 0 ซึ่งจะอ่านว่า "หมดสต๊อก"
 *   redeemedTotal: 0  ⛔ ค่าปลอม คอลัมน์ถูกซ่อน
 *
 * `rewardType` ว่างเกิดได้เฉพาะแถวเก่าที่ไม่มีค่า — ค่าเริ่มต้นของ backend คือ VOUCHER จึงอ่านเป็นคูปอง
 *
 * คูปองชนิดที่หน้าจอยังไม่รองรับ (FREE_PRODUCT · POINT_*) ได้ `voucher: undefined`
 * หน้าจอจะแสดงว่าแก้มูลค่าจากหน้านี้ไม่ได้ และ toCrmRewardPatch จะไม่ส่งค่าคูปองทับ
 *
 * เงื่อนไขการใช้อ่านจาก `usageCondition` — ของรางวัลที่สร้างก่อนมีฟิลด์นี้
 * เก็บเงื่อนไขไว้ใน `description` จึงใช้เป็นค่าสำรอง
 */
export function toUiReward(r: CrmReward): UiReward {
  const type = REWARD_TYPE_FROM_CRM[r.rewardType] ?? 'coupon';

  let voucher: UiReward['voucher'];
  if (type === 'coupon' && (r.voucherType === 'DISCOUNT_FIXED' || r.voucherType === 'DISCOUNT_PERCENT')) {
    const value = decimalOrNull(r.voucherValue);
    voucher = {
      kind: r.voucherType === 'DISCOUNT_PERCENT' ? 'percent' : 'amount',
      value,
      maxDiscount: decimalOrNull(r.voucherMaxAmount),
    };
  }

  return {
    id: String(r.id),
    name: r.name,
    type,
    points: Number(r.points),
    stock: null,
    stockAlertAt: null,
    from: (r.startDatetime ?? '').slice(0, 10),
    to: (r.endDatetime ?? '').slice(0, 10),
    limitPerPerson: null,
    couponValidDays: type === 'product' ? null : r.voucherValidityDays,
    voucher,
    minSpend: type === 'product' ? undefined : (decimalOrNull(r.voucherMinPurchaseAmount) ?? 0),
    quantity: r.quantity ?? 1,
    conditions: r.usageCondition || r.description,
    imageUrl: r.image,
    imageAlt: r.image ? r.name : '',
    enabled: r.isActive !== false,
    redeemedTotal: 0,
    provisional: false,
  };
}

/**
 * แปลงกลับเป็น payload ของ CRM — ส่งเฉพาะฟิลด์ที่ backend รับ
 *
 * ⚠️ วันที่ว่างต้องส่ง `null` ไม่ใช่ `''` ไม่งั้น DRF ตอบ 400 รูปแบบวันที่ผิด
 *    และวันที่จากหน้าจอเป็น YYYY-MM-DD ล้วน ต้องเติมเวลาให้เป็น datetime
 * ⚠️ `rewardType` ส่งเฉพาะตอนสร้าง — เปลี่ยนภายหลังไม่ได้
 * ⚠️ backend สร้าง/แก้ VoucherTemplate ของ BIL ให้เองจาก voucher* ไม่ต้องเรียก BIL ซ้ำ
 *    คูปองแบบลดเป็นบาทไม่มีเพดานในหน้าจอ แต่ backend บังคับ voucher_max_amount
 *    จึงส่งเท่ากับมูลค่า (ส่วนลดคงที่ไม่มีทางเกินมูลค่าตัวเองอยู่แล้ว)
 */
export function toCrmRewardPatch(r: UiReward, isNew: boolean): RewardPatch {
  const patch: RewardPatch = {
    name: r.name,
    usageCondition: r.conditions,
    points: String(r.points),
    startDatetime: r.from ? `${r.from}T00:00:00` : null,
    endDatetime: r.to ? `${r.to}T23:59:59` : null,
    isActive: r.enabled,
    quantity: r.quantity ?? 1,
  };
  if (isNew) patch.rewardType = REWARD_TYPE_TO_CRM[r.type];

  if (r.type === 'coupon' && r.voucher) {
    patch.voucherValidityDays = r.couponValidDays;
    patch.voucherType = r.voucher.kind === 'percent' ? 'DISCOUNT_PERCENT' : 'DISCOUNT_FIXED';
    patch.voucherValue = toDecimal(r.voucher.value);
    patch.voucherMaxAmount = toDecimal(r.voucher.kind === 'amount' ? r.voucher.value : r.voucher.maxDiscount);
    patch.voucherMinPurchaseAmount = toDecimal(r.minSpend ?? 0);
  }
  if (r.type === 'shipping') {
    patch.voucherValidityDays = r.couponValidDays;
    patch.voucherMinPurchaseAmount = toDecimal(r.minSpend ?? 0);
  }
  return patch;
}

/* ------------------------------------------- สิทธิพิเศษแบบกดรับ (FR-ADM-19) */

/**
 * สิ่งที่หน้าสิทธิพิเศษมีแต่ระบบหลังบ้านยังให้ไม่ได้
 *
 * ⛔ "ใช้จริง" นับจากรหัสที่ถูกตัดว่าใช้แล้ว แต่ยังไม่มีที่ไหนในระบบตัดรหัส
 *    ตัวเลขจึงเป็น 0 เสมอ และทำให้ "หมดอายุโดยไม่ได้ใช้" ดูเหมือนเต็ม 100%
 *    ซ่อนไว้ดีกว่าแสดงเลขที่อ่านแล้วสรุปผิด
 */
export const PRIVILEGE_UNSUPPORTED_FIELDS: Array<{ key: 'used'; label: string }> = [
  { key: 'used', label: 'จำนวนที่ใช้จริง' },
];

const PRIVILEGE_STATUS_FROM: Record<string, UiPrivilegeStatus> = {
  DRAFT: 'draft', SCHEDULED: 'scheduled', ACTIVE: 'active', PAUSED: 'paused', ENDED: 'ended',
};
const PRIVILEGE_STATUS_TO: Record<UiPrivilegeStatus, AdminBenefitCode['status']> = {
  draft: 'DRAFT', scheduled: 'SCHEDULED', active: 'ACTIVE', paused: 'PAUSED', ended: 'ENDED',
};

/** ALL ของระบบหลังบ้าน = ใช้ได้ทั้งสองช่องทาง */
const PRIVILEGE_CHANNEL_FROM: Record<string, UiPrivilege['channel']> = {
  ALL: 'both', APP: 'app', STORE: 'pos',
};
const PRIVILEGE_CHANNEL_TO: Record<UiPrivilege['channel'], AdminBenefitCode['usageChannel']> = {
  both: 'ALL', app: 'APP', pos: 'STORE',
};

/** ⚠️ ระบบหลังบ้านใช้ค่าย่อ ไม่ใช่ชื่อเต็ม — ยืนยันจาก response จริงแล้ว */
const PRIVILEGE_BENEFIT_FROM: Record<string, UiPrivilegeBenefitKind> = {
  DP: 'percent', DF: 'amount', FSP: 'shipping', FP: 'product', FSE: 'service',
};
const PRIVILEGE_BENEFIT_TO: Record<UiPrivilegeBenefitKind, AdminBenefitCode['benefitType']> = {
  percent: 'DP', amount: 'DF', shipping: 'FSP', product: 'FP', service: 'FSE',
};

const PRIVILEGE_RECURRENCE_FROM: Record<string, UiPrivilegeRecurrence> = {
  WEEKLY: 'weekly', MONTHLY: 'monthly', YEARLY: 'yearly',
};
const PRIVILEGE_RECURRENCE_TO: Record<UiPrivilegeRecurrence, AdminBenefitCode['recurrencePeriod']> = {
  weekly: 'WEEKLY', monthly: 'MONTHLY', yearly: 'YEARLY',
};

/**
 * แปลงสิทธิพิเศษจากระบบหลังบ้านเป็นรูปที่หน้าจอใช้
 *
 * `tierKeyById` แปลง id ของระดับสมาชิกเป็นคีย์ที่หน้าจอใช้ (silver / gold / platinum)
 * ระดับที่แปลงไม่ได้ถูกตัดออกจากกลุ่มเป้าหมาย และเขียนไว้ในหมายเหตุให้เห็น
 * เพื่อไม่ให้บันทึกทับแล้วสิทธิ์ของระดับนั้นหลุดหายไปเงียบ ๆ
 */
export function toUiPrivilege(b: AdminBenefitCode, tierKeyById: Map<number, UiTierId>): UiPrivilege {
  const tiers: UiTierId[] = [];
  let dropped = 0;
  for (const id of b.loyaltyTierPolicies) {
    const key = tierKeyById.get(id);
    if (key) { if (!tiers.includes(key)) tiers.push(key); } else dropped += 1;
  }

  return {
    id: String(b.id),
    name: b.name || b.code,
    code: b.code,
    description: b.description,
    audience: {
      tiers,
      ...(dropped > 0 ? { note: `อีก ${dropped} ระดับที่หน้านี้ยังไม่รองรับ` } : {}),
    },
    benefitKind: PRIVILEGE_BENEFIT_FROM[b.benefitType] ?? 'percent',
    value: Number(b.value ?? 0),
    maxDiscount: b.maxAmount === null || b.maxAmount === undefined ? null : Number(b.maxAmount),
    minSpend: Number(b.minPurchaseAmount ?? 0),
    allProducts: b.applyTo !== 'SPECIFIC',
    imageUrl: b.image,
    imageAlt: b.image ? b.name : '',
    status: PRIVILEGE_STATUS_FROM[b.status] ?? 'draft',
    from: (b.startDatetime ?? '').slice(0, 10),
    to: (b.endDatetime ?? '').slice(0, 10),
    codeTtlMinutes: b.codeTtlMinutes ?? 15,
    /* null = ไม่จำกัด · เคยแปลงเป็น 1 ซึ่งทำให้หน้าจอบอกว่าจำกัดทั้งที่ไม่จำกัด */
    limitPerPerson: b.perUserLimit,
    totalLimit: b.totalLimit,
    recurrence: PRIVILEGE_RECURRENCE_FROM[b.recurrencePeriod ?? ''] ?? 'monthly',
    hasClaimButton: b.hasClaimButton,
    claimed: b.claimedCount,
    used: b.usedCount,
    expiredUnused: b.expiredUnusedCount,
    channel: PRIVILEGE_CHANNEL_FROM[b.usageChannel] ?? 'both',
    provisional: false,
  };
}

/** ชื่อเต็มของประเภทสิทธิ์ — ใช้เป็นคำนำหน้ารหัส ให้รูปแบบตรงกับที่ระบบหลังบ้านสร้างเอง */
const PRIVILEGE_CODE_PREFIX: Record<UiPrivilegeBenefitKind, string> = {
  percent: 'DISCOUNT_PERCENT',
  amount: 'DISCOUNT_FIXED',
  shipping: 'FREE_SHIPPING',
  product: 'FREE_PRODUCT',
  service: 'FREE_SERVICE',
};

/**
 * 🔧 **ทางเลี่ยงชั่วคราว** — สร้างรหัสสิทธิ์เองตอนสร้างรายการใหม่
 *
 * ปกติไม่ต้องส่ง `code` ระบบหลังบ้านสร้างให้เอง แต่ตอนนี้ขั้นตอนนั้นพัง
 * ถ้าส่ง `benefitType` ไปด้วย จะได้ 500 กลับมาทุกครั้ง (Sentry: MEHEALTH-STAGING-API-EJ)
 * การส่ง `code` ไปเองทำให้ระบบหลังบ้านข้ามขั้นตอนที่พังนั้น
 *
 * รูปแบบเดียวกับที่ระบบหลังบ้านใช้: `{ชื่อประเภท}-{สุ่ม}` ยาวรวม 32 ตัว
 *
 * ⛔ **ลบทิ้งเมื่อ backend แก้แล้ว** — ถ้าปล่อยไว้ เราจะต้องตามแก้ทุกครั้งที่รูปแบบรหัสเปลี่ยน
 */
export function newPrivilegeCode(kind: UiPrivilegeBenefitKind): string {
  const prefix = PRIVILEGE_CODE_PREFIX[kind];
  const length = 32 - prefix.length - 1;
  const bytes = new Uint8Array(Math.ceil(length / 2));
  crypto.getRandomValues(bytes);
  const random = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('').toUpperCase();
  return `${prefix}-${random.slice(0, length)}`;
}

/**
 * แปลงกลับเป็น payload ของระบบหลังบ้าน
 *
 * ⚠️ เงื่อนไขที่ส่งไปมีผลกับ **ทุกระดับที่เลือก** — ตั้งค่าต่างกันรายระดับไม่ได้
 * ⚠️ ไม่ส่ง `code` — รายการใหม่ระบบสร้างรหัสให้เอง ส่วนรายการเดิมห้ามเปลี่ยนรหัส
 * ⚠️ ไม่ส่ง `applyTo` · `applicableProducts` · `freeProduct` — หน้านี้ยังเลือกสินค้าไม่ได้
 *    ถ้าส่งไป สิทธิ์ที่ตั้งเฉพาะสินค้าไว้จากระบบหลังบ้านจะถูกเปลี่ยนเป็นทุกสินค้าทันที
 */
export function toCrmPrivilegePatch(p: UiPrivilege, policyIdByKey: Map<UiTierId, number>): AdminBenefitCodePatch {
  const policies = p.audience.tiers
    .map((key) => policyIdByKey.get(key))
    .filter((id): id is number => typeof id === 'number');

  const kind = p.benefitKind ?? 'percent';

  return {
    name: p.name,
    description: p.description ?? '',
    benefitType: PRIVILEGE_BENEFIT_TO[kind],
    value: String(p.value ?? 0),
    /* เพดานส่วนลดมีความหมายเฉพาะส่วนลดเป็นเปอร์เซ็นต์ — ชนิดอื่นล้างทิ้งกันค่าค้าง */
    maxAmount: kind === 'percent' && p.maxDiscount !== null && p.maxDiscount !== undefined
      ? String(p.maxDiscount)
      : null,
    minPurchaseAmount: String(p.minSpend ?? 0),
    loyaltyTierPolicies: policies,
    /* ⛔ ไม่ส่ง hasClaimButton — ตั้งรายระดับที่หน้าระดับสมาชิก
       ถ้าส่งจากที่นี่ ค่าจะถูกเขียนทับเท่ากันทุกระดับที่เลือก */
    startDatetime: p.from ? `${p.from}T00:00:00` : null,
    endDatetime: p.to ? `${p.to}T23:59:59` : null,
    codeTtlMinutes: p.codeTtlMinutes,
    perUserLimit: p.limitPerPerson,
    totalLimit: p.totalLimit ?? null,
    usageChannel: PRIVILEGE_CHANNEL_TO[p.channel],
    status: PRIVILEGE_STATUS_TO[p.status],
    recurrencePeriod: PRIVILEGE_RECURRENCE_TO[p.recurrence ?? 'monthly'],
  };
}

/* --------------------------------------------------- ประวัติการแก้ไข (AuditLog) */

/**
 * แปลง AuditLog กลางของ HIS เป็น AuditEntry ที่ component AuditTrail ใช้
 *
 * backend บันทึกให้อัตโนมัติทุกครั้งที่สร้าง/แก้/ลบข้อมูลผ่าน API ของ CRM
 * แต่ละรายการมี `before` และ `after` เป็นข้อมูลทั้งแถวในรูปเดียวกับที่ API ส่งออก
 *
 * ⚠️ **ไม่ใช้ฟิลด์ `diff`** — เป็นผลเปรียบเทียบแบบซ้อนชั้น
 *    (`{"values_changed": {"root['name']": {...}}}`) ไม่ใช่ `{field: [before, after]}`
 *    เคยอ่านผิดรูปแบบจนหน้าจอขึ้นชื่อฟิลด์เป็น "values_changed" และค่าเป็น [object Object]
 *    การเทียบ before กับ after เองตรงไปตรงมากว่า และไม่ขึ้นกับรูปแบบของ diff
 *
 * ⚠️ AuditLog **ไม่มีช่องเหตุผล** — ต่างจาก point-claim-action-log ที่มี `comment`
 *    หน้าจอจึงแสดงได้แค่ "ใครแก้อะไรเมื่อไร" ไม่มี "ทำไม"
 *    ถ้าธุรกิจต้องการเหตุผลของการแก้กติกาคะแนน ต้องขอ backend เพิ่มฟิลด์
 */

/** ชื่อที่ต้องหาจากข้อมูลส่วนอื่นของหน้า — ไม่ส่งมา = แสดงเป็นเลขอ้างอิง */
export interface AuditLookup {
  tierName?: (id: number) => string | undefined;
  benefitName?: (id: number) => string | undefined;
}

type AuditFormat =
  | 'text' | 'money' | 'points' | 'count' | 'days' | 'date' | 'limit' | 'multiplier'
  | 'active' | 'yesno' | 'rewardType' | 'voucherType' | 'tier' | 'benefit' | 'benefitList'
  | 'voucherTemplate' | 'image';

/** ฟิลด์ของระบบ — ไม่มีความหมายกับคนตั้งค่า */
const AUDIT_SKIP = new Set([
  'id', 'division', 'created', 'edited', 'edit_user', 'created_at', 'updated_at', 'edited_at',
  'active_benefit_codes',
]);

const AUDIT_FIELD: Record<string, { label: string; format: AuditFormat }> = {
  name: { label: 'ชื่อ', format: 'text' },
  description: { label: 'คำอธิบาย', format: 'text' },
  usage_condition: { label: 'เงื่อนไขการใช้', format: 'text' },
  /* ระดับสมาชิก */
  start_point: { label: 'เกณฑ์ยอดขั้นต่ำ', format: 'money' },
  end_point: { label: 'เกณฑ์ยอดสูงสุด', format: 'money' },
  benefit_codes: { label: 'สิทธิประโยชน์', format: 'benefitList' },
  /* กติกาคะแนน */
  default: { label: 'ใช้เป็นกติกาตั้งต้น', format: 'yesno' },
  start_date: { label: 'เริ่มใช้', format: 'date' },
  end_date: { label: 'ใช้ถึง', format: 'date' },
  tier_multiplier: { label: 'อัตราคะแนนสำหรับเลื่อนระดับ', format: 'multiplier' },
  tier_duration_days: { label: 'รอบการคงระดับ', format: 'days' },
  reward_multiplier: { label: 'อัตราคะแนนสำหรับแลกของ', format: 'multiplier' },
  reward_duration_days: { label: 'อายุคะแนน', format: 'days' },
  /* ของรางวัล */
  points: { label: 'คะแนนที่ใช้แลก', format: 'points' },
  start_datetime: { label: 'เริ่ม', format: 'date' },
  end_datetime: { label: 'ถึง', format: 'date' },
  is_active: { label: 'เปิดให้แลก', format: 'active' },
  reward_type: { label: 'ประเภท', format: 'rewardType' },
  quantity: { label: 'จำนวนที่ได้ต่อการแลก', format: 'count' },
  image: { label: 'รูป', format: 'image' },
  per_user_limit: { label: 'จำกัดต่อคน', format: 'limit' },
  total_limit: { label: 'จำนวนสิทธิ์ทั้งหมด', format: 'limit' },
  voucher_template: { label: 'คูปองที่ผูกไว้', format: 'voucherTemplate' },
  voucher_validity_days: { label: 'อายุคูปองหลังแลก', format: 'days' },
  voucher_type: { label: 'ชนิดส่วนลด', format: 'voucherType' },
  voucher_value: { label: 'มูลค่าส่วนลด', format: 'text' },
  voucher_max_amount: { label: 'ลดสูงสุด', format: 'money' },
  voucher_min_purchase_amount: { label: 'ยอดซื้อขั้นต่ำ', format: 'money' },
  /* ความสัมพันธ์ระดับ ↔ สิทธิ์ */
  benefit_code: { label: 'สิทธิประโยชน์', format: 'benefit' },
  policy: { label: 'ระดับ', format: 'tier' },
};

/** ป้ายเฉพาะหน้า — ฟิลด์ชื่อเดียวกันแต่ความหมายต่างกันตามชนิดข้อมูล */
const AUDIT_FIELD_BY_MODEL: Record<string, Record<string, string>> = {
  loyaltyreward: { start_datetime: 'เริ่มแลกได้', end_datetime: 'แลกได้ถึง' },
  loyaltytierpolicyrelatedcodes: {
    start_datetime: 'เริ่มให้สิทธิ์', end_datetime: 'ให้สิทธิ์ถึง', per_user_limit: 'จำกัดการกดรับต่อคนต่อรอบ',
  },
};

const AUDIT_REWARD_TYPE: Record<string, string> = {
  VOUCHER: 'คูปอง', PRODUCT: 'สินค้าจริง', FREE_SHIPPING: 'ฟรีค่าจัดส่ง',
};
const AUDIT_VOUCHER_TYPE: Record<string, string> = {
  DISCOUNT_FIXED: 'ลดเป็นบาท', DISCOUNT_PERCENT: 'ลดเป็นเปอร์เซ็นต์', FREE_SHIPPING: 'ฟรีค่าจัดส่ง',
  FREE_PRODUCT: 'แถมสินค้าฟรี', POINT_PERCENT: 'ให้คะแนนเป็นเปอร์เซ็นต์', POINT_FIXED: 'ให้คะแนนคงที่',
};

const THAI_NUMBER = (n: number) => n.toLocaleString('th-TH', { maximumFractionDigits: 3 });

/** วันที่แบบ พ.ศ. ตามเวลาท้องถิ่น — ตัดเวลาทิ้ง เพราะช่วงเวลาของโปรแกรมตั้งเป็นรายวัน */
const auditDate = (v: string): string => {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return formatThaiDate(ymd);
};

/**
 * ตัวคูณคะแนนเป็นประโยคที่คนตั้งค่าคุ้นเคย
 *   0.01 → "ซื้อ 100 บาท ได้ 1 คะแนน" · 2 → "ซื้อ 1 บาท ได้ 2 คะแนน"
 * ตัวคูณที่ไม่ลงตัวแสดงเป็นตัวเลขตรง ๆ ดีกว่าปัดแล้วบอกค่าที่ไม่จริง
 */
const auditMultiplier = (m: number): string => {
  if (m <= 0) return THAI_NUMBER(m);
  if (m >= 1) return `ซื้อ 1 บาท ได้ ${THAI_NUMBER(m)} คะแนน`;
  const per = 1 / m;
  return Math.abs(per - Math.round(per)) < 1e-6
    ? `ซื้อ ${THAI_NUMBER(Math.round(per))} บาท ได้ 1 คะแนน`
    : `× ${THAI_NUMBER(m)}`;
};

function auditFormat(format: AuditFormat, v: unknown, lookup: AuditLookup): string | null {
  const num = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;

  switch (format) {
    case 'limit': return v === null || v === undefined || v === '' ? 'ไม่จำกัด' : `${THAI_NUMBER(num)} ครั้ง`;
    case 'active': return v ? 'เปิด' : 'ปิด';
    case 'yesno': return v ? 'ใช่' : 'ไม่ใช่';
    case 'image': return v ? 'มีรูป' : 'ไม่มีรูป';
    case 'voucherTemplate': return v === null || v === undefined || v === '' ? 'ไม่มี' : `คูปอง #${String(v)}`;
    case 'benefitList': {
      if (!Array.isArray(v) || v.length === 0) return 'ไม่มี';
      return v.map((id) => lookup.benefitName?.(Number(id)) ?? `รหัสสิทธิ์ #${String(id)}`).join(', ');
    }
    default: break;
  }

  if (v === null || v === undefined || v === '') return null;

  switch (format) {
    case 'money': return Number.isNaN(num) ? String(v) : `${THAI_NUMBER(num)} บาท`;
    case 'points': return Number.isNaN(num) ? String(v) : `${THAI_NUMBER(num)} คะแนน`;
    case 'count': return Number.isNaN(num) ? String(v) : THAI_NUMBER(num);
    case 'days': return Number.isNaN(num) ? String(v) : `${THAI_NUMBER(num)} วัน`;
    case 'multiplier': return Number.isNaN(num) ? String(v) : auditMultiplier(num);
    case 'date': return auditDate(String(v));
    case 'rewardType': return AUDIT_REWARD_TYPE[String(v)] ?? String(v);
    case 'voucherType': return AUDIT_VOUCHER_TYPE[String(v)] ?? String(v);
    case 'tier': return lookup.tierName?.(Number(v)) ?? `ระดับ #${String(v)}`;
    case 'benefit': return lookup.benefitName?.(Number(v)) ?? `รหัสสิทธิ์ #${String(v)}`;
    default:
      if (typeof v === 'boolean') return v ? 'ใช่' : 'ไม่ใช่';
      if (typeof v === 'object') return JSON.stringify(v);
      return String(v);
  }
}

const AUDIT_NOUN: Record<string, string> = {
  loyaltytierpolicy: 'ระดับสมาชิก',
  loyaltyearningpolicy: 'กติกาคะแนน',
  loyaltyreward: 'ของรางวัล',
};
const AUDIT_VERB: Record<string, string> = { Create: 'สร้าง', Update: 'แก้ไข', Delete: 'ลบ' };
const AUDIT_ICON: Record<string, string> = { Create: 'add_circle', Update: 'edit', Delete: 'delete' };

/** หัวข้อของรายการ — บอกให้ได้ว่าแก้ "อะไร" ไม่ใช่แค่ "แก้ไขค่า" */
function auditHeadline(log: AuditLogEntry, lookup: AuditLookup): { action: string; icon: string } {
  const row = (log.action === 'Delete' ? log.before : log.after) ?? {};
  const icon = AUDIT_ICON[log.action] ?? 'history';

  if (log.model === 'loyaltytierpolicyrelatedcodes') {
    const benefit = auditFormat('benefit', row.benefit_code, lookup) ?? 'สิทธิประโยชน์';
    const tier = auditFormat('tier', row.policy, lookup) ?? 'ระดับ';
    if (log.action === 'Create') return { action: `ผูกสิทธิ์ "${benefit}" เข้าระดับ ${tier}`, icon: 'link' };
    if (log.action === 'Delete') return { action: `ถอดสิทธิ์ "${benefit}" ออกจากระดับ ${tier}`, icon };
    return { action: `แก้เงื่อนไขสิทธิ์ "${benefit}" ของระดับ ${tier}`, icon };
  }

  const verb = AUDIT_VERB[log.action] ?? log.action;
  const noun = AUDIT_NOUN[log.model] ?? 'รายการ';
  const name = typeof row.name === 'string' && row.name.trim() ? ` "${row.name.trim()}"` : '';
  const suffix = log.model === 'loyaltyearningpolicy' && row.default ? ' (ค่าตั้งต้น)' : '';
  return { action: `${verb}${noun}${name}${suffix}`, icon };
}

export function toAuditEntries(logs: AuditLogEntry[], lookup: AuditLookup = {}): AuditEntry[] {
  return logs
    .map((log): AuditEntry => {
      const user = log.editUser;
      const { action, icon } = auditHeadline(log, lookup);
      const before = log.before ?? {};
      const after = log.after ?? {};
      const labels = AUDIT_FIELD_BY_MODEL[log.model] ?? {};
      const isCreate = log.action === 'Create';

      /* ถูกผูกลงในหัวข้อแล้ว ไม่ต้องซ้ำในตาราง */
      const inHeadline = log.model === 'loyaltytierpolicyrelatedcodes' ? new Set(['benefit_code', 'policy']) : new Set<string>();

      /* ลบ: หัวข้อบอกครบแล้วว่าลบอะไร รายการค่าทั้งแถวไม่ช่วยให้เข้าใจเพิ่ม */
      const changes = log.action === 'Delete'
        ? []
        : Array.from(new Set([...Object.keys(before), ...Object.keys(after)]))
            .filter((field) => !AUDIT_SKIP.has(field) && !inHeadline.has(field))
            .map((field) => {
              const spec = AUDIT_FIELD[field] ?? { label: field, format: 'text' as AuditFormat };
              return {
                field: labels[field] ?? spec.label,
                before: isCreate ? null : auditFormat(spec.format, before[field], lookup),
                after: auditFormat(spec.format, after[field], lookup),
              };
            })
            /* สร้าง: แสดงเฉพาะช่องที่มีค่า · แก้: แสดงเฉพาะช่องที่ค่าที่อ่านได้เปลี่ยนจริง
               (เทียบหลังจัดรูปแบบ — "1.0" กับ "1.000" ไม่นับว่าเปลี่ยน) */
            .filter((c) => (isCreate ? c.after !== null : c.before !== c.after));

      return {
        id: `${log.model}-${log.objectId}-${log.created}`,
        at: log.created,
        actorName: user ? (user.fullName || user.username) : 'ระบบ',
        actorRole: user ? 'เจ้าหน้าที่' : '—',
        action,
        icon,
        automated: !user,
        initial: isCreate,
        changes,
      };
    })
    /* ใหม่สุดอยู่บน — backend เรียงมาให้แล้ว แต่เรียงซ้ำกันพลาด */
    .sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
}

/* ------------------------------------------------------------------ คูปอง */

/**
 * VoucherTemplate ของ BIL → Coupon ที่หน้าจอใช้
 *
 * ⚠️ backend มีชนิดคูปอง **6 แบบ** แต่หน้าจอออกแบบไว้ 3 แบบ
 *    FREE_PRODUCT (แถมสินค้าฟรี) · POINT_PERCENT · POINT_FIXED (ให้คะแนน)
 *    ยังไม่มีที่แสดง — แปลงเป็น 'amount' แล้วให้ container แจ้งว่าแก้ที่นี่ไม่ได้
 *    ดีกว่าซ่อนคูปองพวกนั้นหายไป ซึ่งคนตั้งค่าจะไม่รู้ว่ามีอยู่
 */
const VOUCHER_KIND: Record<string, UiCouponKind> = {
  DISCOUNT_FIXED: 'amount',
  DISCOUNT_PERCENT: 'percent',
  FREE_SHIPPING: 'freeship',
};

/** ชนิดที่หน้าจอยังไม่รองรับ — container ใช้ล็อกไม่ให้แก้ */
export const UNSUPPORTED_VOUCHER_TYPES = ['FREE_PRODUCT', 'POINT_PERCENT', 'POINT_FIXED'];

const VOUCHER_STACK: Record<string, UiStackRule> = {
  CANNOT_COMBINE: 'exclusive',
  MEMBER_TIER_DISCOUNT: 'with-tier',
  POINT_REDEMPTION: 'with-points',
  ALL: 'all',
};

const VOUCHER_STATUS: Record<string, UiCouponStatus> = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  PAUSED: 'paused',
  EXPIRED: 'expired',
  OUT_OF_QUOTA: 'exhausted',
  /* ⛔ หน้าจอไม่มีสถานะ "ยกเลิก" — แสดงเป็นพักไว้ ซึ่งใกล้เคียงที่สุด
        และ container แจ้งว่าแก้สถานะของคูปองที่ยกเลิกแล้วไม่ได้ */
  CANCELED: 'paused',
};

export function toUiCoupon(v: VoucherTemplate): UiCoupon {
  const kind = VOUCHER_KIND[v.voucherType] ?? 'amount';

  return {
    id: String(v.id),
    /* โค้ดที่ลูกค้ากรอกคือ voucher_code · `code` เป็นรหัสสินค้าของระบบ Product */
    code: v.voucherCode || v.code,
    name: v.name || v.internalName,
    kind,
    value: v.value === null || v.value === '' ? null : Number(v.value),
    maxDiscount: v.maxAmount === null || v.maxAmount === '' ? null : Number(v.maxAmount),
    minSpend: v.minPurchaseAmount ? Number(v.minPurchaseAmount) : 0,
    /**
     * ⛔ BIL ไม่มีวันเริ่มใช้งาน — มีแต่ expiry_mode / validity_days / expired_at
     *    ใช้วันสร้างแทนเพื่อให้ตารางเรียงได้ และแจ้งผ่าน COUPON_UNSUPPORTED_FIELDS
     *    ว่าแก้ไม่ได้ (ช่องที่กรอกได้แต่บันทึกไม่ขึ้นคือกับดักที่ร้ายกว่าไม่มีช่อง)
     */
    from: (v.createdAt ?? '').slice(0, 10),
    expiresAt: (v.expiredAt ?? '').slice(0, 10),
    totalQuota: v.totalQuota ?? null,
    usedCount: v.totalUsed ?? 0,
    /* null = ไม่จำกัด · หน้าจอคาดหวังตัวเลข จึงใช้ 0 แทนความหมาย "ไม่จำกัด" ไม่ได้
       ใช้ค่าที่ backend ให้ ถ้าไม่มีถือว่า 1 ตามพฤติกรรมคูปองทั่วไป */
    perPerson: v.maxUsesPerPatient ?? 1,
    stackRule: VOUCHER_STACK[v.combinationCondition] ?? 'exclusive',
    status: VOUCHER_STATUS[v.computedStatus] ?? 'draft',
    batch:
      v.codeType === 'BATCH'
        ? {
            /* voucher_code ของชุดคือคำนำหน้าล้วน — codeTypeDetail.prefix มี "-******" ต่อท้ายมาแล้ว
               ไม่ใช้ค่านั้นเพราะหน้าจอต่อตัวปิดบังเองอีกชั้น */
            prefix: v.voucherCode,
            size: v.batchQuantity ?? 0,
            issued: v.totalCodesGenerated ?? 0,
          }
        : null,
    createdBy: v.createdByName || '—',
    imageUrl: v.image,
    imageAlt: v.image ? (v.name || v.internalName) : '',
    provisional: false,
  };
}

/** Coupon ที่หน้าจอแก้แล้ว → payload ของ BIL */
export function toVoucherTemplatePatch(c: UiCoupon): VoucherTemplatePatch {
  const typeOf: Record<UiCouponKind, string> = {
    amount: 'DISCOUNT_FIXED',
    percent: 'DISCOUNT_PERCENT',
    freeship: 'FREE_SHIPPING',
  };
  const stackOf: Record<UiStackRule, string> = {
    exclusive: 'CANNOT_COMBINE',
    'with-tier': 'MEMBER_TIER_DISCOUNT',
    'with-points': 'POINT_REDEMPTION',
    all: 'ALL',
  };
  const statusOf: Record<string, string> = {
    draft: 'DRAFT', active: 'ACTIVE', paused: 'PAUSED',
    /* expired / exhausted เป็นสถานะที่ backend คำนวณเอง ส่งกลับไปไม่ได้
       ส่ง ACTIVE แทน แล้วให้ backend คำนวณใหม่จาก expired_at และโควตา */
    expired: 'ACTIVE', exhausted: 'ACTIVE',
  };

  return {
    name: c.name,
    /* ชุดโค้ด: voucher_code คือคำนำหน้า (A–Z 0–9 ≤ 12 ตัว) ส่วนสุ่มต่อท้าย backend สร้างเอง */
    voucherCode: c.batch ? c.batch.prefix : c.code,
    voucherType: typeOf[c.kind] as VoucherTemplate['voucherType'],
    /* value รับ null ไม่ได้ ("may not be null") แต่ backend แปลง '' เป็น 0 ให้ — ใช้กับฟรีค่าส่ง */
    value: c.value === null ? '' : String(c.value),
    maxAmount: c.maxDiscount === null ? null : String(c.maxDiscount),
    minPurchaseAmount: String(c.minSpend),
    /**
     * ⚠️ ขาส่งรับ **วันที่ล้วน YYYY-MM-DD** — ส่ง datetime ได้ 400 "วันที่ผิดรูปแบบ"
     *    backend ตั้งเวลาเป็นสิ้นวันตามเวลาท้องถิ่นให้เอง
     *    ค่าว่างไม่ส่งเลย เพราะส่ง null ตอนแก้ได้ 400 และตอนสร้าง backend บังคับอยู่แล้ว
     */
    ...(c.expiresAt ? { expiredAt: c.expiresAt } : {}),
    totalQuota: c.totalQuota,
    maxUsesPerPatient: c.perPerson,
    combinationCondition: stackOf[c.stackRule] as VoucherTemplate['combinationCondition'],
    status: statusOf[c.status] as VoucherTemplate['status'],
    codeType: c.batch ? 'BATCH' : 'SINGLE',
    batchQuantity: c.batch?.size ?? null,
    ...(c.batch?.codeLength ? { randomCodeLength: c.batch.codeLength } : {}),
  };
}
